import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MdSearch,
  MdChevronLeft,
  MdChevronRight,
} from 'react-icons/md';
import employeeAttendanceHrAPI from '../../api/employeeAttendanceHrAPI';
import { useEmployeeAttendanceAuth } from '../../context/EmployeeAttendanceAuthContext';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import { fetchWithHrToken, HR_SESSION_ERROR } from '../../utils/hrApiHelpers';
import HrPortalAccessModal from './HrPortalAccessModal';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';

const PAGE_SIZE = 80;
const RECORD_NAME = 'Employee Attendance Portal Access';

function formatCreatedOn(emp) {
  const raw =
    emp.portal_created_on ||
    emp.created_on ||
    emp.create_date ||
    emp.write_date ||
    emp.portal_create_date;
  if (!raw || raw === false) return '';
  const d = new Date(String(raw).includes('T') ? raw : `${String(raw).replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getUsername(emp) {
  const login = emp?.user_login || emp?.login || emp?.work_email || emp?.portal_login;
  if (!login || login === false) return '';
  return String(login);
}

function hasPortalUser(emp) {
  if (emp?.has_portal_access === true) return true;
  if (emp?.portal_user_created === true) return true;
  return Boolean(getUsername(emp) && emp?.user_id);
}

/**
 * Full-page Odoo wizard: Attendance → Employee Attendance Portal Access → New
 */
function PortalAccessFormPage({
  mode,
  employee,
  allEmployees,
  selectedId,
  onSelectId,
  options,
  faculties,
  onOptionChange,
  onCreate,
  onDiscard,
  onNew,
  saving,
  error,
  resultPreview,
}) {
  const onlyWithoutPortal = options.showOnlyWithoutPortal;
  const employeeOptions = useMemo(() => {
    const list = allEmployees || [];
    if (onlyWithoutPortal) return list.filter((e) => !hasPortalUser(e));
    return list;
  }, [allEmployees, onlyWithoutPortal]);

  const selectedEmployee =
    mode === 'edit'
      ? employee
      : employeeOptions.find((e) => String(e.id) === String(selectedId));

  return (
    <div className="ea-odoo-form-page ea-odoo-portal-wizard">
      <div className="ea-odoo-form-bar">
        <div className="ea-odoo-form-bar__left">
          <button type="button" className="ea-odoo-new" onClick={onNew}>
            New
          </button>
          <nav className="ea-odoo-breadcrumb" aria-label="Breadcrumb">
            <button type="button" className="ea-odoo-breadcrumb__btn" onClick={onDiscard}>
              {RECORD_NAME}
            </button>
            <span className="ea-odoo-breadcrumb__sep">/</span>
            <span className="ea-odoo-breadcrumb__current">{RECORD_NAME}</span>
          </nav>
        </div>
        <div className="ea-odoo-form-bar__right">
          <button
            type="button"
            className="ea-odoo-btn ea-odoo-btn--primary"
            onClick={onCreate}
            disabled={saving || (!selectedId && mode === 'new')}
            title="Save"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="ea-odoo-btn" onClick={onDiscard} disabled={saving}>
            Discard
          </button>
        </div>
      </div>

      {error && (
        <div className="ea-odoo-alert ea-odoo-alert--error" role="alert">
          {error}
        </div>
      )}

      <form
        className="ea-odoo-sheet ea-odoo-portal-wizard__sheet"
        onSubmit={(e) => {
          e.preventDefault();
          onCreate();
        }}
      >
        <div className="ea-odoo-portal-wizard__top">
          <div className="ea-odoo-portal-wizard__col">
            <div className="ea-odoo-field">
              <label className="ea-odoo-field__label">Record name</label>
              <div className="ea-odoo-field__value">
                <input value={RECORD_NAME} readOnly />
              </div>
            </div>
            <div className="ea-odoo-field">
              <label className="ea-odoo-field__label">
                Employee
                <span className="ea-odoo-help" title="Employee who will receive portal login">
                  ?
                </span>
              </label>
              <div className="ea-odoo-field__value">
                {mode === 'edit' ? (
                  <input value={employee?.name || ''} readOnly />
                ) : (
                  <select
                    value={selectedId}
                    onChange={(e) => onSelectId(e.target.value)}
                    required
                    autoFocus
                  >
                    <option value="">—</option>
                    {employeeOptions.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>

          <div className="ea-odoo-portal-wizard__col ea-odoo-portal-wizard__checks">
            <label className="ea-odoo-check">
              <input
                type="checkbox"
                checked={options.autoGeneratePassword}
                onChange={(e) =>
                  onOptionChange({ autoGeneratePassword: e.target.checked })
                }
              />
              <span>Auto-generate passwords</span>
            </label>
            <label className="ea-odoo-check">
              <input
                type="checkbox"
                checked={options.grantHrDashboard}
                onChange={(e) =>
                  onOptionChange({ grantHrDashboard: e.target.checked })
                }
              />
              <span>HR dashboard access</span>
            </label>
            <label className="ea-odoo-check">
              <input
                type="checkbox"
                checked={options.grantAdmissionPortal}
                onChange={(e) =>
                  onOptionChange({ grantAdmissionPortal: e.target.checked })
                }
              />
              <span>Admission portal access</span>
            </label>
            <label className="ea-odoo-check">
              <input
                type="checkbox"
                checked={options.grantExamPortal}
                onChange={(e) =>
                  onOptionChange({ grantExamPortal: e.target.checked })
                }
              />
              <span>Exam portal access</span>
            </label>
            <label className="ea-odoo-check">
              <input
                type="checkbox"
                checked={options.grantPostgraduatePortal}
                onChange={(e) =>
                  onOptionChange({ grantPostgraduatePortal: e.target.checked })
                }
              />
              <span>Postgraduate portal access</span>
            </label>

            <label className="ea-odoo-check">
              <input
                type="checkbox"
                checked={options.grantFacultyAdminPortal}
                onChange={(e) =>
                  onOptionChange({ grantFacultyAdminPortal: e.target.checked })
                }
              />
              <span>Faculty Admin portal access</span>
            </label>
            {options.grantFacultyAdminPortal && (
              <div className="ea-odoo-field" style={{ alignItems: 'flex-start' }}>
                <span className="ea-odoo-field__label">Managed Faculties*</span>
                <div className="ea-odoo-field__value" style={{ maxHeight: 180, overflowY: 'auto' }}>
                  {faculties.map((faculty) => (
                    <label key={faculty.id} className="ea-odoo-check">
                      <input
                        type="checkbox"
                        checked={(options.managedFacultyIds || []).includes(Number(faculty.id))}
                        onChange={(event) => {
                          const current = options.managedFacultyIds || [];
                          const id = Number(faculty.id);
                          onOptionChange({
                            managedFacultyIds: event.target.checked
                              ? [...new Set([...current, id])]
                              : current.filter((value) => value !== id),
                          });
                        }}
                      />
                      <span>{faculty.name}</span>
                    </label>
                  ))}
                  {!faculties.length && <span className="ea-odoo-muted">No faculties available.</span>}
                </div>
              </div>
            )}
            <label className="ea-odoo-check">
              <input
                type="checkbox"
                checked={options.sendCredentialsEmail}
                onChange={(e) =>
                  onOptionChange({ sendCredentialsEmail: e.target.checked })
                }
              />
              <span>Send credentials via email</span>
            </label>
          </div>
        </div>

        <section className="ea-odoo-portal-wizard__section">
          <h3 className="ea-odoo-group__title">Configuration filters</h3>
          <label className="ea-odoo-check">
            <input
              type="checkbox"
              checked={options.showOnlyWithoutPortal}
              onChange={(e) =>
                onOptionChange({ showOnlyWithoutPortal: e.target.checked })
              }
              disabled={mode === 'edit'}
            />
            <span>Show only employees without portal access</span>
          </label>
        </section>

        <section className="ea-odoo-portal-wizard__section">
          <h3 className="ea-odoo-group__title">Results</h3>
          {resultPreview ? (
            <div className="ea-odoo-portal-wizard__results">
              <p>
                <strong>Status:</strong> {resultPreview.status || 'created'}
              </p>
              <p>
                <strong>Login:</strong>{' '}
                {resultPreview.portal_user?.login ||
                  resultPreview.employee?.user_login ||
                  '—'}
              </p>
              {resultPreview.portal_user?.password ? (
                <p>
                  <strong>Password:</strong> {resultPreview.portal_user.password}
                </p>
              ) : (
                <p className="ea-odoo-muted">
                  No password returned (user may already exist, or backend did not
                  generate one).
                </p>
              )}
            </div>
          ) : selectedEmployee ? (
            <p className="ea-odoo-muted">
              Selected: <strong>{selectedEmployee.name}</strong>
              {getUsername(selectedEmployee)
                ? ` · ${getUsername(selectedEmployee)}`
                : ' · no portal login yet'}
            </p>
          ) : (
            <p className="ea-odoo-muted">Select an employee, then create the portal user.</p>
          )}
        </section>

        <div className="ea-odoo-portal-wizard__footer">
          <button
            type="submit"
            className="ea-odoo-portal-wizard__create"
            disabled={saving || (mode === 'new' && !selectedId)}
          >
            {saving ? 'Creating…' : 'Create Employee Attendance Portal user'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Odoo menu: Attendance → Employee Attendance Portal Access
 */
export default function HrPortalAccessPage() {
  const { ensureHrToken } = useEmployeeAttendanceHrAuth();
  const { user: staffUser } = useEmployeeAttendanceAuth();

  const [portalRows, setPortalRows] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [portalResult, setPortalResult] = useState(null);
  const [formResultPreview, setFormResultPreview] = useState(null);
  const [form, setForm] = useState(null);
  const [listSource, setListSource] = useState('odoo'); // 'odoo' | 'fallback'
  const [options, setOptions] = useState({
    autoGeneratePassword: true,
    grantHrDashboard: false,
    grantAdmissionPortal: false,
    grantExamPortal: false,
    grantPostgraduatePortal: false,
    grantFacultyAdminPortal: false,
    managedFacultyIds: [],
    sendCredentialsEmail: true,
    showOnlyWithoutPortal: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    const all = await fetchWithHrToken(ensureHrToken, (token) =>
      employeeAttendanceHrAPI.getEmployees(
        { active: true, limit: 500, offset: 0 },
        token
      )
    );

    if (all.error === 'no_token') {
      setError(staffUser?.hr_api_error || HR_SESSION_ERROR);
      setPortalRows([]);
      setAllEmployees([]);
      setLoading(false);
      return;
    }

    setAllEmployees(all.data?.employees || []);
    const facultyResult = await fetchWithHrToken(ensureHrToken, (token) =>
      employeeAttendanceHrAPI.getPortalAccessFaculties(token)
    );
    setFaculties(facultyResult.data?.faculties || []);

    // Prefer real Odoo Portal Access records (same 15 as Odoo tree view)
    const accessList = await fetchWithHrToken(ensureHrToken, (token) =>
      employeeAttendanceHrAPI.getPortalAccessList(
        { limit: 500, offset: 0, search: undefined },
        token
      )
    );

    if (!accessList.error) {
      const records =
        accessList.data?.records ||
        accessList.data?.portal_access ||
        accessList.data?.items ||
        [];
      setListSource('odoo');
      setPortalRows(
        records.map((r, idx) => ({
          id: r.id ?? `pa-${r.employee_id}-${idx}`,
          employee_id: r.employee_id || r.employee?.id,
          name: r.employee_name || r.employee?.name || r.name,
          user_login:
            r.username ||
            r.user_login ||
            r.login ||
            r.portal_user?.login ||
            '',
          has_portal_access: Boolean(
            r.portal_user_created ?? r.has_portal_access ?? r.user_id
          ),
          create_date: r.create_date || r.created_on || r.portal_created_on,
          grant_hr_dashboard_access: Boolean(
            r.grant_hr_dashboard_access ?? r.grantHrDashboard
          ),
          grant_admission_portal_access: Boolean(
            r.grant_admission_portal_access ?? r.grantAdmissionPortal
          ),
          grant_exam_portal_access: Boolean(
            r.grant_exam_portal_access ?? r.grantExamPortal
          ),
          grant_postgraduate_portal_access: Boolean(
            r.grant_postgraduate_portal_access ?? r.grantPostgraduatePortal
          ),

          grant_faculty_admin_portal_access: Boolean(
            r.grant_faculty_admin_portal_access ?? r.grantFacultyAdminPortal
          ),
          faculty_admin_faculty_ids: (r.faculty_admin_faculty_ids || []).map(Number),
          _raw: r,
        }))
      );
      setSelected(new Set());
      setLoading(false);
      return;
    }

    // No list API yet — do NOT use /portal-employees (that is all portal staff, e.g. 143)
    if (accessList.code === 404 || /404|not found/i.test(accessList.error || '')) {
      setListSource('fallback');
      setPortalRows([]);
      setError(null);
      setLoading(false);
      return;
    }

    setError(accessList.error);
    setPortalRows([]);
    setLoading(false);
  }, [ensureHrToken, staffUser?.hr_api_error]);

  useEffect(() => {
    const t = setTimeout(() => {
      setOffset(0);
      setSearch(searchInput.trim().toLowerCase());
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    if (!search) return portalRows;
    return portalRows.filter((e) => {
      const hay = [
        e.name,
        e.record_name,
        getUsername(e),
        e.department_name,
        e.department,
        e.job_title,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(search);
    });
  }, [portalRows, search]);

  const total = filteredRows.length;
  const pageRows = filteredRows.slice(offset, offset + PAGE_SIZE);
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;
  const allSelected =
    pageRows.length > 0 && pageRows.every((r) => selected.has(r.id));

  const openNew = () => {
    setFormError(null);
    setFormResultPreview(null);
    setOptions({
      autoGeneratePassword: true,
      grantHrDashboard: false,
      grantAdmissionPortal: false,
      grantExamPortal: false,
    grantPostgraduatePortal: false,
      grantFacultyAdminPortal: false,
      managedFacultyIds: [],
      sendCredentialsEmail: true,
      showOnlyWithoutPortal: true,
    });
    setForm({ mode: 'new', selectedId: '' });
  };

  const openEdit = (emp) => {
    setFormError(null);
    setFormResultPreview(null);
    setOptions((prev) => ({
      ...prev,
      grantHrDashboard: Boolean(
        emp.grant_hr_dashboard_access ?? emp._raw?.grant_hr_dashboard_access
      ),
      grantAdmissionPortal: Boolean(
        emp.grant_admission_portal_access ?? emp._raw?.grant_admission_portal_access
      ),
      grantExamPortal: Boolean(
        emp.grant_exam_portal_access ?? emp._raw?.grant_exam_portal_access
      ),
      grantPostgraduatePortal: Boolean(
        emp.grant_postgraduate_portal_access ?? emp._raw?.grant_postgraduate_portal_access
      ),

      grantFacultyAdminPortal: Boolean(
        emp.grant_faculty_admin_portal_access ?? emp._raw?.grant_faculty_admin_portal_access
      ),
      managedFacultyIds: (
        emp.faculty_admin_faculty_ids || emp._raw?.faculty_admin_faculty_ids || []
      ).map(Number),
    }));
    setForm({ mode: 'edit', employee: emp, selectedId: String(emp.id) });
  };

  const grantPortal = async () => {
    const employeeId =
      form?.mode === 'new'
        ? form.selectedId
        : form?.employee?.employee_id || form?.employee?.id;
    if (!employeeId) {
      setFormError('Please select an employee.');
      return;
    }

    if (options.grantFacultyAdminPortal && !(options.managedFacultyIds || []).length) {
      setFormError('Select at least one Managed Faculty for Faculty Admin access.');
      return;
    }

    setSaving(true);
    setFormError(null);

    // Only documented HR API params — extra keys can break portal user creation
    const params = {
      employee_id: Number(employeeId),
      auto_generate_password: Boolean(options.autoGeneratePassword),
      grant_hr_dashboard_access: Boolean(options.grantHrDashboard),
      grant_admission_portal_access: Boolean(options.grantAdmissionPortal),
      grant_exam_portal_access: Boolean(options.grantExamPortal),
      grant_postgraduate_portal_access: Boolean(options.grantPostgraduatePortal),
      grant_faculty_admin_portal_access: Boolean(options.grantFacultyAdminPortal),
      ...(options.grantFacultyAdminPortal
        ? { faculty_admin_faculty_ids: options.managedFacultyIds || [] }
        : {}),
    };

    const { data, error: fetchError, result } = await fetchWithHrToken(
      ensureHrToken,
      (token) => employeeAttendanceHrAPI.createPortalAccess(params, token)
    );
    setSaving(false);

    if (fetchError === 'no_token') {
      setFormError(HR_SESSION_ERROR);
      return;
    }
    if (fetchError) {
      setFormError(fetchError);
      return;
    }

    const portalUser = data?.portal_user || result?.portal_user || {};
    const login = portalUser.login || data?.employee?.user_login || '';
    const password = portalUser.password;

    if (!login) {
      setFormError(
        'Server did not create a portal login (no username returned). ' +
          'In Odoo you may see a Portal Access row with an empty Username — ' +
          'ask backend to fix POST /api/employee-attendance/hr/portal-access/create ' +
          'so it creates res.users and returns portal_user.login + portal_user.password.'
      );
      setFormResultPreview(data);
      return;
    }

    setFormResultPreview(data);
    setPortalResult(data);
    await load();
  };

  if (form) {
    return (
      <>
        <PortalAccessFormPage
          mode={form.mode}
          employee={form.employee}
          allEmployees={allEmployees}
          selectedId={form.selectedId || ''}
          onSelectId={(id) => setForm((f) => ({ ...f, selectedId: id }))}
          options={options}
          faculties={faculties}
          onOptionChange={(patch) => setOptions((o) => ({ ...o, ...patch }))}
          onCreate={grantPortal}
          onDiscard={() => {
            setForm(null);
            setFormError(null);
            setFormResultPreview(null);
          }}
          onNew={openNew}
          saving={saving}
          error={formError}
          resultPreview={formResultPreview}
        />
        {portalResult && (
          <HrPortalAccessModal
            result={portalResult}
            onClose={() => setPortalResult(null)}
          />
        )}
      </>
    );
  }

  return (
    <div className="ea-hr-dash ea-odoo-emp">
      <div className="ea-odoo-control">
        <div className="ea-odoo-control__left">
          <button type="button" className="ea-odoo-new" onClick={openNew}>
            New
          </button>
          <div className="ea-odoo-control__title">{RECORD_NAME}</div>
        </div>

        <div className="ea-odoo-search">
          <MdSearch className="ea-odoo-search__icon" aria-hidden />
          <input
            type="search"
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search portal access"
          />
        </div>

        <div className="ea-odoo-control__right">
          <div className="ea-odoo-pager" aria-label="Pagination">
            <span>
              {loading ? '…' : `${rangeStart}-${rangeEnd} / ${total}`}
            </span>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canPrev || loading}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              aria-label="Previous page"
            >
              <MdChevronLeft size={20} />
            </button>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canNext || loading}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              aria-label="Next page"
            >
              <MdChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="ea-toast error show ea-dash-error" role="alert">
          {error}
        </div>
      )}

      {listSource === 'fallback' && (
        <div className="ea-hr-api-banner" role="status">
          <strong>Wrong list API was used before (that showed ~143).</strong> Odoo’s
          Portal Access form has its own records (your Odoo shows <strong>15</strong>),
          not all portal employees.
          <br />
          Ask backend to add:{' '}
          <code>POST /api/employee-attendance/hr/portal-access</code> (list) returning
          the same rows as Odoo: record id, employee, username, portal_user_created,
          create_date. Until then this page stays empty so the count matches Odoo once
          the API exists — it will not invent 143 employees.
        </div>
      )}

      <div className="ea-odoo-list-wrap">
        <table className="ea-odoo-list ea-odoo-att-list">
          <thead>
            <tr>
              <th className="ea-odoo-att-list__check">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => {
                    if (allSelected) setSelected(new Set());
                    else setSelected(new Set(pageRows.map((r) => r.id)));
                  }}
                  aria-label="Select all"
                />
              </th>
              <th>Record name</th>
              <th>Employee</th>
              <th>Username</th>
              <th>Portal user created</th>
              <th>Created on</th>
            </tr>
          </thead>
          <tbody>
            {loading && pageRows.length === 0 ? (
              <tr>
                <td colSpan={6} className="ea-hr-table__empty">
                  Loading…
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={6} className="ea-hr-table__empty">
                  {search
                    ? 'No portal users match your search.'
                    : 'No portal users yet. Click New and create a portal user (must return username).'}
                </td>
              </tr>
            ) : (
              pageRows.map((emp, idx) => (
                <tr
                  key={emp.id}
                  className={`ea-odoo-att-list__row${idx % 2 ? ' ea-odoo-list__stripe' : ''}`}
                  onClick={() => openEdit(emp)}
                >
                  <td
                    className="ea-odoo-att-list__check"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(emp.id)}
                      onChange={() => {
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (next.has(emp.id)) next.delete(emp.id);
                          else next.add(emp.id);
                          return next;
                        });
                      }}
                      aria-label={`Select ${emp.name}`}
                    />
                  </td>
                  <td>{emp.record_name || RECORD_NAME}</td>
                  <td className="ea-odoo-list__name">{emp.name}</td>
                  <td title={getUsername(emp)}>{getUsername(emp)}</td>
                  <td className="ea-odoo-att-list__check">
                    <input
                      type="checkbox"
                      checked={hasPortalUser(emp)}
                      readOnly
                      tabIndex={-1}
                      aria-label="Portal user created"
                    />
                  </td>
                  <td>{formatCreatedOn(emp)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {portalResult && (
        <HrPortalAccessModal result={portalResult} onClose={() => setPortalResult(null)} />
      )}
    </div>
  );
}
